import { useState } from 'react';
import { Modal, Button, Input, Label, Spinner } from '@theraply/ui';
import { AvailabilityException } from '@theraply/api-client';

interface ExceptionManagerProps {
  exceptions: AvailabilityException[];
  isLoading: boolean;
  isOpen: boolean;
  onAddException: (data: {
    date: string;
    allDay: boolean;
    startTime?: string;
    endTime?: string;
    reason?: string;
  }) => void;
  onDeleteException: (id: number) => void;
  onClose: () => void;
}

export default function ExceptionManager({
  exceptions,
  isLoading,
  isOpen,
  onAddException,
  onDeleteException,
  onClose,
}: ExceptionManagerProps) {
  const [date, setDate] = useState('');
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAddException = () => {
    setError(null);

    if (!date) {
      setError('Date is required');
      return;
    }

    if (!allDay && (!startTime || !endTime)) {
      setError('Start time and end time are required');
      return;
    }

    if (!allDay && startTime >= endTime) {
      setError('Start time must be before end time');
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    if (date < today) {
      setError('Cannot add exceptions for past dates');
      return;
    }

    onAddException({
      date,
      allDay,
      startTime: !allDay ? startTime : undefined,
      endTime: !allDay ? endTime : undefined,
      reason: reason || undefined,
    });

    // Reset form
    setDate('');
    setAllDay(true);
    setStartTime('');
    setEndTime('');
    setReason('');
  };

  const handleDeleteException = (id: number) => {
    if (window.confirm('Are you sure you want to remove this exception?')) {
      onDeleteException(id);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Manage Availability Exceptions
        </h2>

        <div className="space-y-6">
          {/* Add Exception Form */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add Exception</h3>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label htmlFor="exception-date">Date</Label>
                <Input
                  id="exception-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={allDay}
                    onChange={(e) => setAllDay(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">All Day</span>
                </label>
              </div>

              {!allDay && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="exception-start">Start Time</Label>
                    <Input
                      id="exception-start"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="exception-end">End Time</Label>
                    <Input
                      id="exception-end"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="exception-reason">Reason (optional)</Label>
                <textarea
                  id="exception-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Conference, Personal Day, Emergency"
                  rows={2}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <Button
                onClick={handleAddException}
                disabled={isLoading}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white"
              >
                {isLoading ? <Spinner /> : 'Add Exception'}
              </Button>
            </div>
          </div>

          {/* Exceptions List */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Blocked Dates ({exceptions.length})
            </h3>

            {exceptions.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No exceptions added yet
              </p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {exceptions.map((exception) => (
                  <div
                    key={exception.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {new Date(exception.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                      <div className="text-sm text-gray-600">
                        {exception.allDay ? (
                          <span>All day blocked</span>
                        ) : (
                          <span>
                            {exception.startTime} - {exception.endTime}
                          </span>
                        )}
                      </div>
                      {exception.reason && (
                        <p className="text-sm text-gray-500 mt-1">{exception.reason}</p>
                      )}
                    </div>
                    <Button
                      onClick={() => handleDeleteException(exception.id)}
                      disabled={isLoading}
                      className="ml-4 bg-red-600 hover:bg-red-700 text-white text-sm"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-900"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
